import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  BookOpen, 
  Search, 
  ArrowRight,
  GraduationCap,
  Brain,
  Calculator,
  Sparkles,
  Target,
  Award,
  Trophy,
  Users,
  HelpCircle,
  FileText,
  MessageCircle,
  Video,
  MonitorPlay,
  UsersRound,
  Globe,
  Building,
  PlayCircle
} from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useMemo } from "react";

interface Platform {
  name: string;
  slug: string;
  description: string;
  category: string;
  icon: typeof BookOpen;
}

const platforms: Platform[] = [
  // General Academic Support
  {
    name: "Khan Academy",
    slug: "khan-academy",
    description: "Free nonprofit learning platform with thousands of video lessons and practice exercises across math, science, history, and more.",
    category: "General Academic Support",
    icon: GraduationCap,
  },
  {
    name: "Quizlet",
    slug: "quizlet",
    description: "Popular study tool centered on user-generated flashcards and interactive practice activities for memorization.",
    category: "General Academic Support",
    icon: Brain,
  },
  {
    name: "Wolfram Alpha",
    slug: "wolfram-alpha",
    description: "Computational knowledge engine for math, science, and factual queries with step-by-step solutions.",
    category: "General Academic Support",
    icon: Calculator,
  },
  {
    name: "SparkNotes",
    slug: "sparknotes",
    description: "Long-standing resource for literature study guides with chapter summaries, character analyses, and No Fear Shakespeare translations.",
    category: "General Academic Support",
    icon: BookOpen,
  },
  // Test Preparation
  {
    name: "Magoosh",
    slug: "magoosh",
    description: "Affordable, self-paced test prep for GRE, GMAT, LSAT, SAT, ACT with video lessons and extensive practice questions.",
    category: "Test Preparation",
    icon: Target,
  },
  {
    name: "Kaplan Test Prep",
    slug: "kaplan",
    description: "Premium test preparation courses for SAT, ACT, GRE, GMAT, LSAT, MCAT with live classes and private tutoring options.",
    category: "Test Preparation",
    icon: Award,
  },
  {
    name: "The Princeton Review",
    slug: "princeton-review",
    description: "Leading test prep provider with rigorous curricula, score improvement guarantees, and expert instructors.",
    category: "Test Preparation",
    icon: Trophy,
  },
  {
    name: "Fiveable",
    slug: "fiveable",
    description: "Community-based platform for AP exam prep with live study sessions, cram nights, and collaborative study rooms.",
    category: "Test Preparation",
    icon: Users,
  },
  // Tutoring & Homework Help
  {
    name: "Chegg",
    slug: "chegg",
    description: "On-demand homework help with step-by-step textbook solutions, expert Q&A, math solver, and writing tools.",
    category: "Tutoring & Homework Help",
    icon: HelpCircle,
  },
  {
    name: "Course Hero",
    slug: "course-hero",
    description: "Access millions of course-specific study materials, lecture notes, past exams, and 24/7 homework help from tutors.",
    category: "Tutoring & Homework Help",
    icon: FileText,
  },
  {
    name: "Brainly",
    slug: "brainly",
    description: "Crowd-sourced homework Q&A platform where students help other students with academic questions.",
    category: "Tutoring & Homework Help",
    icon: MessageCircle,
  },
  // Collaborative Study Tools
  {
    name: "Focusmate",
    slug: "focusmate",
    description: "Virtual body-doubling platform that pairs you with a partner for live, webcam-based co-working sessions.",
    category: "Collaborative Study Tools",
    icon: Video,
  },
  {
    name: "StudyStream",
    slug: "studystream-platform",
    description: "24/7 virtual study rooms where hundreds of students join via video to study together in real time.",
    category: "Collaborative Study Tools",
    icon: MonitorPlay,
  },
  {
    name: "Study Together",
    slug: "study-together",
    description: "Massive Discord community with 24/7 focus rooms, free tutoring, and gamified study tracking.",
    category: "Collaborative Study Tools",
    icon: UsersRound,
  },
  // Skill-Based Learning
  {
    name: "Coursera",
    slug: "coursera",
    description: "Leading MOOC platform with 5,000+ courses from top universities like Stanford and Yale, plus professional certificates from Google and IBM.",
    category: "Skill-Based Learning",
    icon: Globe,
  },
  {
    name: "edX",
    slug: "edx",
    description: "University-level courses from Harvard and MIT with MicroMasters programs that can count toward full degrees.",
    category: "Skill-Based Learning",
    icon: Building,
  },
  {
    name: "Udemy",
    slug: "udemy",
    description: "Vast course marketplace with 200,000+ affordable courses on any topic, from programming to personal development.",
    category: "Skill-Based Learning",
    icon: PlayCircle,
  },
];

const categories = ["All", "General Academic Support", "Test Preparation", "Tutoring & Homework Help", "Collaborative Study Tools", "Skill-Based Learning"];

const OnlineStudy = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredPlatforms = useMemo(() => {
    return platforms.filter(platform => {
      const matchesSearch = platform.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          platform.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === "All" || platform.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory]);

  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Online Study Platforms | CSW",
    "description": "Explore and compare top online study platforms including Khan Academy, Quizlet, Wolfram Alpha, and more.",
    "url": "https://csw.live/online-study/",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": platforms.map((platform, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": platform.name,
        "url": `https://csw.live/online-study/${platform.slug}/`
      }))
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Online Study Platforms | CSW | Compare Study Tools"
        description="Explore and compare top online study platforms including Khan Academy, Quizlet, Wolfram Alpha, and more. Find the best study tools for your learning needs."
        keywords="online study platforms, Khan Academy, Quizlet, Wolfram Alpha, study tools, learning platforms, academic support"
        canonicalUrl="/online-study/"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdSchema) }}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">STUDY PLATFORM REVIEWS</span>
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              Online <span className="text-gradient">Study Platforms</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Discover and compare the best online study tools and learning platforms for academic success.
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 relative">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search platforms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-6 text-lg bg-card border-border"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? "bg-primary text-primary-foreground" : ""}
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Platforms Grid */}
      <section className="py-12 relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            {filteredPlatforms.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPlatforms.map((platform) => {
                  const Icon = platform.icon;
                  return (
                    <Link key={platform.slug} to={`/online-study/${platform.slug}/`}>
                      <Card className="group h-full bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 cursor-pointer">
                        <CardContent className="p-6 space-y-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Icon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <span className="text-xs font-medium text-primary/70">{platform.category}</span>
                            <h3 className="text-xl font-semibold text-foreground font-display mt-1">
                              {platform.name}
                            </h3>
                          </div>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {platform.description}
                          </p>
                          <div className="flex items-center gap-2 text-primary font-medium">
                            Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">No platforms found matching your search.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/5 to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto p-12 sm:p-16 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/50 border border-border mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">FREE STUDY PLATFORM</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Study Better with <span className="text-gradient">CSW</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of students using CSW for free virtual study rooms, productivity tools, flashcards, quizzes, and community learning.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Try CSW Free <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OnlineStudy;
